<template>
  <div class="tw-flex tw-flex-col tw-gap-4" :data-testid="testid">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-4">
      <span class="tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
        {{ label }}
      </span>
      <SfButtonDs
        v-if="adding"
        ref="addButtonEl"
        variant="secondary"
        size="sm"
        icon-before="plus"
        :aria-expanded="picking"
        :data-testid="`${testid}-add`"
        @click="togglePicker"
      >
        {{ adding.label }}
      </SfButtonDs>
    </div>

    <div v-if="adding && picking" ref="pickerEl">
      <AccessPicker
        :placeholder="adding.placeholder"
        :search="adding.search"
        @select="onSelect"
      />
    </div>

    <SfSearchInput
      v-if="searchable"
      :model-value="paging.query"
      :placeholder="findPlaceholder"
      :aria-label="findPlaceholder"
      :show-result-navigation="false"
      :data-testid="`${testid}-find`"
      wide
      autocomplete="off"
      @update:model-value="emit('search', $event ?? '')"
      @clear="emit('search', '')"
    />

    <p
      v-if="paging.loading && !paging.loadingMore && items.length"
      class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2"
      :data-testid="`${testid}-searching`"
    >
      {{ I18n.t('common.loading') }}
    </p>

    <div
      class="tw-max-h-60 tw-overflow-y-auto"
      :aria-busy="paging.loading ? 'true' : undefined"
    >
      <SfInpageList v-if="items.length">
        <SfInpageListItem
          v-for="item in items"
          :key="item.key"
          :title="item.title"
          :item-id="item.key"
          :icon-size="iconSize"
        >
          <template #icon>
            <slot name="icon" :item="item">
              <span
                class="fa tw-text-sf-slate"
                :class="`fa-${icon}`"
                aria-hidden="true"
              />
            </slot>
          </template>
          <template v-if="item.subtitle" #subtitles>
            <SfInpageListItemSubtitle>
              {{ item.subtitle }}
            </SfInpageListItemSubtitle>
          </template>
          <template #right>
            <SfButtonDs
              v-if="item.removable"
              variant="secondary"
              size="sm"
              :aria-label="
                I18n.t('user.panel.remove_item', { name: item.title })
              "
              @click="emit('remove', item.key)"
            >
              {{ I18n.t('user.panel.remove') }}
            </SfButtonDs>
          </template>
        </SfInpageListItem>
      </SfInpageList>

      <p v-else-if="paging.loading" :class="ACCESS_LIST_MESSAGE_CLASS">
        {{ I18n.t('common.loading') }}
      </p>

      <p
        v-else-if="!paging.failed && paging.query.trim()"
        :class="ACCESS_LIST_MESSAGE_CLASS"
      >
        {{ I18n.t('user.panel.no_assigned_matches', { query: paging.query }) }}
      </p>

      <p v-else-if="!paging.failed" :class="ACCESS_LIST_MESSAGE_CLASS">
        {{ emptyLabel }}
      </p>

      <div
        v-if="paging.failed"
        class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-4"
      >
        <p class="tw-m-0 tw-text-sm tw-text-sf-danger">
          {{ I18n.t('user.panel.search_error') }}
        </p>
        <SfButtonDs
          variant="secondary"
          size="sm"
          :data-testid="`${testid}-retry`"
          @click="emit('search', paging.query)"
        >
          {{ I18n.t('user.panel.retry') }}
        </SfButtonDs>
      </div>

      <div v-if="paging.hasMore" class="tw-mt-3 tw-flex tw-justify-center">
        <SfButtonDs
          variant="secondary"
          size="sm"
          :loading="paging.loadingMore"
          :data-testid="`${testid}-load-more`"
          @click="emit('load-more')"
        >
          {{
            I18n.t('user.panel.load_more', {
              shown: items.length,
              total: paging.matchCount,
            })
          }}
        </SfButtonDs>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends AutocompleteOption">
import type {
  AccessListAdding,
  AccessListIcon,
  AccessListItem,
  AccessListPaging,
  AutocompleteOption,
} from '@app/application/user-management/types'

import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'

import {
  ACCESS_LIST_MESSAGE_CLASS,
  ACCESS_PAGE_SIZE,
} from '@app/application/user-management/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'
import AccessPicker from '@app/application/user-management/components/user-panel/access-picker.vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfInpageList from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list.vue'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'
import SfSearchInput from '@app/shared/vue/components/sf-search/search-input.vue'

defineOptions({ name: 'AccessList' })

const props =
  defineProps<{
    testid: string
    icon: AccessListIcon
    label: string
    emptyLabel: string
    findPlaceholder: string
    items: AccessListItem[]
    paging: AccessListPaging
    adding?: AccessListAdding<T>
    iconSize?: number
  }>()

const emit =
  defineEmits<{
    add: [option: T]
    remove: [key: AccessListItem['key']]
    search: [query: string]
    'load-more': []
  }>()

const I18n = useI18n()

const picking = ref(false)
const pickerEl = ref<HTMLElement | null>(null)
const addButtonEl = ref<InstanceType<typeof SfButtonDs> | null>(null)

// The Add button toggles the picker itself, so a click on it is not a click
// outside.
onClickOutside(
  pickerEl,
  () => {
    picking.value = false
  },
  { ignore: [addButtonEl] }
)

// Removing rows under a query can bring the count down to one page, which
// would hide the field that holds the query.
const searchable = computed(
  () => props.paging.count > ACCESS_PAGE_SIZE || props.paging.query !== ''
)

// A row added under a search shows only if it matches that search.
function togglePicker(): void {
  picking.value = !picking.value

  if (picking.value && props.paging.query !== '') emit('search', '')
}

defineSlots<{
  icon?(props: { item: AccessListItem }): unknown
}>()

function onSelect(option: T): void {
  picking.value = false

  emit('add', option)
}
</script>
